import { Group } from '@gripp/shared-logic';
import { StyleProp } from 'react-native';
import { Chip } from 'react-native-paper';
import { useMemo } from 'react';
import { useRoleHex } from '../../themes';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

type GroupChipProps = {
  group: Group;
  onPress?: (groupId: string) => Promise<void>;
  additionalStyle?: StyleProp<any>;
};

export const GroupChip = (props: GroupChipProps) => {
  const onPress = async (groupId: string) => {
    if (props.onPress) {
      await props.onPress(groupId);
    }
  };

  const chipBase = useGroupChipStyle();
  const chipStyle = props.additionalStyle
    ? [chipBase, props.additionalStyle]
    : [chipBase];

  return (
    <Chip
      onPress={async () => await onPress(props.group.id)}
      textStyle={styles.chipText}
      style={chipStyle}
      ellipsizeMode="tail"
    >
      {props.group.name}
    </Chip>
  );
};

const styles = themedStyleSheet((role) => ({
  chipText: {
    lineHeight: 14,
    fontWeight: '400',
    color: role.textStrong,
    paddingVertical: 1,
    marginLeft: 10,
    marginRight: 10,
    marginTop: 5,
    marginBottom: 5,
  },
}));

// A function, not a captured `const`: `styles.chipText` reads through the
// `themedStyleSheet` Proxy, which resolves live on every access. Capturing
// the read once at module-eval time would freeze it at whichever mode was
// active on import — callers must invoke this at render time instead.
export const getGroupChipTextStyle = () => styles.chipText;

/**
 * Paper's `Chip` flattens the `style` it is handed and runs the resulting
 * `backgroundColor` through the `color` library (getChipColors ->
 * getSelectedBackgroundColor -> Color(...).mix(...)). A `var()` string is not
 * parseable, so it throws `Unable to parse color from string: var(--x)` and
 * takes the whole screen down through the error boundary.
 *
 * So unlike every other style object in the app, this one background cannot be
 * a CSS variable. `useRoleHex` resolves it to the mode's real hex and
 * re-renders on a mode flip. The text colour is fine as a variable: Paper does
 * no maths on `textStyle`.
 */
export const useGroupChipStyle = () => {
  const backgroundColor = useRoleHex('surfaceHover');
  return useMemo(() => ({ backgroundColor }), [backgroundColor]);
};
